// Polymarket US wire shapes → the UI's integer units (MC prices, MQ quantities, Cash); pure functions, no transport.

import { type Cash, type MC, type MQ, countToMq, dollarsToCash, dollarsToMc } from '../core/money';

type Money = { value?: string | number | null; currency?: string } | null | undefined;

const mcOf = (m: Money | string | number): MC | null => (
  m && typeof m === 'object' ? dollarsToMc(m.value) : dollarsToMc(m as string | number)
);
const cashOf = (m: Money): Cash => dollarsToCash(m?.value) ?? 0;
const mqOf = (v: string | number | null | undefined): MQ => countToMq(v) ?? 0;

export interface PmMarket {
  slug: string;
  /** SPORTS_MARKET_TYPE_MONEYLINE / SPREAD / TOTAL / PROP / FUTURE, or ''. */
  sportsType: string;
  /** Live shape: `title` is the outcome ("New York Mets"), `question` the event ("National League Champion"). */
  title: string;
  question: string;
  /** What to show: "question · title", or whichever exists. */
  label: string;
  subtitle: string;
  endDate: string;
  category: string;
  active: boolean;
  closed: boolean;
  volume24h: number;
  /** Price step in MC; 0.01 → 10, 0.001 → 1. */
  tickMc: number;
  bestBidMc: MC | null;
  bestAskMc: MC | null;
}

export interface PmLevel {
  priceMc: MC;
  mq: MQ;
}

/** An event with its markets, from `/v1/search` and `/v1/events/slug/{slug}` (markets are inlined in full). */
export interface PmEvent {
  slug: string;
  title: string;
  category: string;
  tags: string[];
  /** Team names and abbreviations, for sports. */
  teams: string[];
  startTime: string;
  live: boolean;
  markets: PmMarket[];
}

export function parseEvent(e: any): PmEvent {
  return {
    slug: String(e?.slug ?? ''),
    title: String(e?.title ?? e?.slug ?? ''),
    category: String(e?.category ?? ''),
    tags: ((e?.tags ?? []) as any[]).map((t) => String(t?.label ?? '')).filter(Boolean),
    teams: ((e?.teams ?? []) as any[]).flatMap((t) => [t?.name, t?.abbreviation]).map((x) => String(x ?? '')).filter(Boolean),
    startTime: String(e?.startTime ?? e?.startDate ?? ''),
    live: Boolean(e?.live),
    markets: ((e?.markets ?? []) as any[]).map(parseMarket).filter((m) => m.slug),
  };
}

/** The order inside a CreateOrder response: `{ id, executions: [{ order, type }] }`, tolerating a bare order object. */
export function parsePlaceResponse(body: any): PmOrder {
  const execs = (body?.executions ?? []) as any[];
  // Executions are a history: NEW first, then fills; the last one carries the order's actual final state.
  const withOrder = execs.filter((e) => e?.order);
  const raw = withOrder[withOrder.length - 1]?.order ?? body?.order ?? body ?? {};
  const o = parseOrder(raw);
  if (!o.id && body?.id) o.id = String(body.id);
  // A rejected execution with no state on the order is still a rejection.
  if (execs.some((e) => String(e?.type ?? '').endsWith('REJECTED')) && !/CANCELED|REJECTED/.test(o.state)) o.state = 'ORDER_STATE_REJECTED';
  return o;
}

/** One of OUR fills; `isAggressor` picks which counterparty's execution in the trade activity is ours. */
export interface PmFill {
  fillId: string;
  tradeId: string;
  orderId: string;
  slug: string;
  /** epoch ms */
  t: number;
  /** 'yes' bought YES, 'no' sold YES — the app's buy-normalised convention. */
  side: 'yes' | 'no';
  yesPriceMc: MC;
  mq: MQ;
  feeCash: Cash;
  isTaker: boolean;
}

export function parseTradeActivity(a: any): PmFill | null {
  const t = a?.trade;
  if (!t) return null;
  const mine = t.isAggressor ? t.aggressorExecution : t.passiveExecution;
  const order = mine?.order ?? (t.isAggressor ? t.aggressor : t.passive);
  if (!mine || !order) return null;
  const px = mcOf(mine.lastPx) ?? mcOf(t.price);
  const mq = mqOf(mine.lastShares) || mqOf(t.qtyDecimal ?? t.qty);
  if (px === null || !mq) return null;
  const ts = Date.parse(String(mine.transactTime ?? t.createTime ?? ''));
  return {
    fillId: String(mine.id ?? `${t.id}:${t.isAggressor ? 'a' : 'p'}`),
    tradeId: String(t.id ?? ''),
    orderId: String(order.id ?? ''),
    slug: String(t.marketSlug ?? order.marketSlug ?? ''),
    t: Number.isFinite(ts) ? ts : Date.now(),
    side: String(order.side ?? '').endsWith('SELL') ? 'no' : 'yes',
    yesPriceMc: px,
    mq,
    feeCash: cashOf(mine.commissionNotionalCollected),
    isTaker: Boolean(t.isAggressor),
  };
}

/** A market resolving: before/after carry cumulative realised P&L when present, else it's a closing trade at $1/$0. */
export interface PmResolution {
  slug: string;
  t: number;
  realizedDeltaCash: Cash | null;
  /** Settlement YES price, when the realised figure is absent. */
  settleMc: MC | null;
  beforeNetMq: MQ;
}

export function parseResolutionActivity(a: any): PmResolution | null {
  const r = a?.positionResolution ?? a?.resolution ?? a;
  const slug = String(r?.marketSlug ?? r?.afterPosition?.marketMetadata?.slug ?? '');
  if (!slug) return null;
  const before = r?.beforePosition;
  const after = r?.afterPosition;
  const realizedDelta = after?.realized && before?.realized ? cashOf(after.realized) - cashOf(before.realized) : null;
  const side = String(r?.side ?? '');
  const ts = Date.parse(String(r?.updateTime ?? ''));
  return {
    slug,
    t: Number.isFinite(ts) ? ts : Date.now(),
    realizedDeltaCash: realizedDelta,
    settleMc: side.endsWith('LONG') ? 1000 : side.endsWith('SHORT') ? 0 : null,
    beforeNetMq: mqOf(before?.netPositionDecimal ?? before?.netPosition),
  };
}

/** One execution from the private stream's order channel. */
export interface PmExecution {
  order: PmOrder;
  /** PARTIAL_FILL, FILL, CANCELED, REPLACE, REJECTED, EXPIRED, DONE_FOR_DAY. */
  type: string;
  /** Shares in this execution (fills only), MQ. */
  lastMq: MQ;
  /** YES price of this execution (fills only). */
  lastMc: MC | null;
  tradeId: string;
}

export function parseExecution(e: any): PmExecution {
  return {
    order: parseOrder(e?.order ?? {}),
    type: String(e?.type ?? '').replace(/^EXECUTION_TYPE_/, ''),
    lastMq: mqOf(e?.lastShares),
    lastMc: mcOf(e?.lastPx),
    tradeId: String(e?.tradeId ?? e?.id ?? ''),
  };
}

/** A position from the stream's before/after pair: net and cost only, no mark. */
export function parsePositionDelta(slug: string, after: any): { slug: string; netMq: MQ; costCash: Cash; realizedCash: Cash | null } {
  return {
    slug,
    netMq: mqOf(after?.netPositionDecimal ?? after?.netPosition),
    costCash: cashOf(after?.cost),
    realizedCash: after?.realized ? cashOf(after.realized) : null,
  };
}

/** Top of book plus the venue's only volume figures. GET /v1/markets/{slug}/bbo. */
export interface PmBbo {
  slug: string;
  bidMc: MC | null;
  askMc: MC | null;
  lastMc: MC | null;
  /** Shares traded over the market's life, MQ. */
  sharesTradedMq: MQ;
  openInterestMq: MQ;
  bidDepth: number;
  askDepth: number;
}

export function parseBbo(md: any): PmBbo {
  return {
    slug: String(md?.marketSlug ?? ''),
    bidMc: mcOf(md?.bestBid),
    askMc: mcOf(md?.bestAsk),
    lastMc: mcOf(md?.lastTradePx) ?? mcOf(md?.currentPx),
    sharesTradedMq: mqOf(md?.sharesTraded),
    openInterestMq: mqOf(md?.openInterest),
    bidDepth: Number(md?.bidDepth ?? 0) || 0,
    askDepth: Number(md?.askDepth ?? 0) || 0,
  };
}

export interface PmBook {
  slug: string;
  /** Highest first. */
  bids: PmLevel[];
  /** Lowest first. */
  offers: PmLevel[];
  state: string;
  lastTradeMc: MC | null;
  /** YES settlement price once the market has resolved (0 is a real answer: NO won); null before. */
  settlementMc: MC | null;
  sharesTradedMq: MQ;
  openInterestMq: MQ;
  /** ms */
  t: number;
}

export interface PmBalance {
  currency: string;
  balanceCash: Cash;
  buyingPowerCash: Cash;
  openOrdersCash: Cash;
  unsettledCash: Cash;
}

export interface PmPosition {
  slug: string;
  title: string;
  outcome: string;
  /** Signed shares, MQ. */
  netMq: MQ;
  costCash: Cash;
  realizedCash: Cash;
  cashValueCash: Cash;
  avgMc: MC | null;
  markMc: MC | null;
  unrealizedCash: Cash;
  expired: boolean;
}

export interface PmOrder {
  id: string;
  slug: string;
  side: 'buy' | 'sell';
  /** Which contract the order is on; `priceMc` is ALWAYS the YES price regardless. */
  outcome: 'yes' | 'no';
  /** Which side of the YES ladder the order rests on ('y' bids, 'n' asks); derived once here from whichever fields the venue sent. */
  ladder: 'y' | 'n';
  intent: string;
  /** YES price (the venue quotes every order on the long side). */
  priceMc: MC | null;
  qtyMq: MQ;
  leavesMq: MQ;
  /** Filled so far. */
  cumMq: MQ;
  /** Average fill price (YES), when the venue reports one. */
  avgMc: MC | null;
  state: string;
  tif: string;
  createTime: string;
}

// Exported so the shapes can be unit-tested without a transport.

export function parseMarket(m: any): PmMarket {
  const title = String(m.title ?? '');
  const question = String(m.question ?? '');
  return {
    slug: String(m.slug ?? ''),
    title: title || question || String(m.slug ?? ''),
    question,
    label: question && title && question !== title ? `${question} · ${title}` : (title || question || String(m.slug ?? '')),
    subtitle: String(m.subtitle ?? ''),
    endDate: String(m.endDate ?? ''),
    sportsType: String(m.sportsMarketTypeV2 ?? '').replace(/^SPORTS_MARKET_TYPE_/, ''),
    category: String(m.category ?? ''),
    active: Boolean(m.active),
    closed: Boolean(m.closed),
    volume24h: Number(m.volume24hr ?? m.volume ?? 0) || 0,
    tickMc: Math.max(1, Math.round(Number(m.orderPriceMinTickSize ?? 0.01) * 1000) || 10),
    bestBidMc: mcOf(m.bestBidQuote),
    bestAskMc: mcOf(m.bestAskQuote),
  };
}

export function parseBook(md: any): PmBook {
  const level = (l: any): PmLevel | null => {
    const priceMc = mcOf(l?.px);
    return priceMc === null ? null : { priceMc, mq: mqOf(l?.qty) };
  };
  const bids = ((md?.bids ?? []) as any[]).map(level).filter((l): l is PmLevel => !!l)
    .sort((a, b) => b.priceMc - a.priceMc);
  const offers = ((md?.offers ?? []) as any[]).map(level).filter((l): l is PmLevel => !!l)
    .sort((a, b) => a.priceMc - b.priceMc);
  const t = Date.parse(String(md?.transactTime ?? ''));
  return {
    slug: String(md?.marketSlug ?? ''),
    bids,
    offers,
    state: String(md?.state ?? ''),
    // Docs say lastTradePx/sharesTraded; the live book actually uses currentPx / lastPriceSample.longPx / openInterest.
    lastTradeMc: mcOf(md?.stats?.lastTradePx) ?? mcOf(md?.stats?.currentPx) ?? mcOf(md?.stats?.lastPriceSample?.longPx),
    settlementMc: mcOf(md?.stats?.settlementPx),
    sharesTradedMq: mqOf(md?.stats?.sharesTraded),
    openInterestMq: mqOf(md?.stats?.openInterest),
    t: Number.isFinite(t) ? t : Date.now(),
  };
}

export function parseBalance(b: any): PmBalance {
  const c = (v: any): Cash => dollarsToCash(v && typeof v === 'object' ? v.value : v) ?? 0;
  return {
    currency: String(b?.currency ?? 'USD'),
    balanceCash: c(b?.currentBalance),
    buyingPowerCash: c(b?.buyingPower),
    openOrdersCash: c(b?.openOrders),
    unsettledCash: c(b?.unsettledFunds),
  };
}

/** `positions` comes keyed by slug; the slug is passed in beside the record. */
export function parsePosition(slug: string, p: any): PmPosition {
  const netMq = mqOf(p?.netPositionDecimal);
  const costCash = cashOf(p?.cost);
  // Docs mislabel cashValue as unrealized PnL; it's actually market value (cost + unrealized).
  const unrealizedCash = cashOf(p?.cashValue);
  const cashValueCash = costCash + unrealizedCash;
  // cost (1e-6 $) over qty (1e-3 shares) is $/share in 1e-3 — exactly MC.
  const per = (cash: Cash): MC | null => (netMq ? Math.round(Math.abs(cash / netMq)) : null);
  return {
    slug,
    title: String(p?.marketMetadata?.title ?? slug),
    outcome: String(p?.marketMetadata?.outcome ?? ''),
    netMq,
    costCash,
    realizedCash: cashOf(p?.realized),
    cashValueCash,
    avgMc: per(costCash),
    markMc: per(cashValueCash),
    unrealizedCash,
    expired: Boolean(p?.expired),
  };
}

export function parseOrder(o: any): PmOrder {
  // The venue fills unset enums with *_UNSPECIFIED / *_UNDEFINED; those say nothing.
  const enumOr = (v: unknown): string => { const s = String(v ?? ''); return /UNSPECIFIED|UNDEFINED$/.test(s) ? '' : s; };
  const intent = enumOr(o?.intent);
  const action = enumOr(o?.action);
  const outcomeSide = enumOr(o?.outcomeSide);
  const rawSide = enumOr(o?.side);
  // Precedence: (outcomeSide, action) pair, then intent, then side (LONG's side — NO buy shows as SELL).
  let ladder: 'y' | 'n';
  if (outcomeSide && action) ladder = (outcomeSide.endsWith('NO')) === (action.endsWith('SELL')) ? 'y' : 'n';
  else if (intent) ladder = intent.endsWith('BUY_LONG') || intent.endsWith('SELL_SHORT') ? 'y' : 'n';
  else ladder = rawSide.endsWith('SELL') ? 'n' : 'y';
  const outcome: 'yes' | 'no' = outcomeSide ? (outcomeSide.endsWith('NO') ? 'no' : 'yes') : intent.endsWith('SHORT') ? 'no' : 'yes';
  const side: 'buy' | 'sell' = action ? (action.endsWith('SELL') ? 'sell' : 'buy') : (ladder === 'y') === (outcome === 'yes') ? 'buy' : 'sell';
  return {
    id: String(o?.id ?? ''),
    slug: String(o?.marketSlug ?? ''),
    side,
    outcome,
    ladder,
    intent,
    priceMc: mcOf(o?.price),
    qtyMq: mqOf(o?.quantity),
    leavesMq: mqOf(o?.leavesQuantity),
    cumMq: mqOf(o?.cumQuantity),
    avgMc: mcOf(o?.avgPx),
    state: String(o?.state ?? ''),
    tif: String(o?.tif ?? ''),
    createTime: String(o?.createTime ?? ''),
  };
}
